import { Box, Popover } from '@mui/material';
import { useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';

// -- type definitions --

export interface ColorPopoverPickerProps {
  value: string;
  onChange: (value: string) => void;
}

interface RgbaColorLike {
  r: number;
  g: number;
  b: number;
  a: number;
}

// -- functions --

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const parseRgbLikeToRgba = (value: string): RgbaColorLike | null => {
  const rgbMatch = value.match(
    /^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*((?:\d*\.\d+)|\d+))?\)$/i
  );

  if (!rgbMatch) {
    return null;
  }

  return {
    r: clamp(Number(rgbMatch[1]), 0, 255),
    g: clamp(Number(rgbMatch[2]), 0, 255),
    b: clamp(Number(rgbMatch[3]), 0, 255),
    a: clamp(
      Number.isFinite(Number(rgbMatch[4])) ? Number(rgbMatch[4]) : 1,
      0,
      1
    ),
  };
};

const parseColorToRgba = (value: string): RgbaColorLike => {
  const trimmed = value.trim();

  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);

    if (hex.length === 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: 1,
      };
    }

    if (hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: parseInt(hex.slice(6, 8), 16) / 255,
      };
    }
  }

  const parsedRgb = parseRgbLikeToRgba(trimmed);
  if (parsedRgb) {
    return parsedRgb;
  }

  return { r: 25, g: 118, b: 210, a: 1 };
};

const toRgbaString = (rgba: RgbaColorLike): string => {
  const alpha = Math.round(clamp(rgba.a, 0, 1) * 100) / 100;
  return `rgba(${Math.round(clamp(rgba.r, 0, 255))}, ${Math.round(
    clamp(rgba.g, 0, 255)
  )}, ${Math.round(clamp(rgba.b, 0, 255))}, ${alpha})`;
};

// -- main component --

export function ColorPopoverPicker({
  value,
  onChange,
}: ColorPopoverPickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const rgbaColor = parseColorToRgba(value);

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event: React.MouseEvent<HTMLElement>) =>
          setAnchorEl(event.currentTarget)
        }
        sx={{
          width: 30,
          height: 30,
          borderRadius: 1,
          backgroundColor: value,
          border: '1px solid rgba(0,0,0,0.22)',
          cursor: 'pointer',
          padding: 0,
          display: 'block',
          flexShrink: 0,
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box p={1.25}>
          <RgbaColorPicker
            color={rgbaColor}
            onChange={(next) => onChange(toRgbaString(next))}
          />
        </Box>
      </Popover>
    </>
  );
}
